import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  ChannelsResponse,
  GatesResponse,
  NotificationsOverview,
  ProviderEvent,
  ProviderEventsParams,
  ProviderEventsResponse,
  ProviderSendersResponse,
  ProviderTemplate,
  ProviderTemplateDetailResponse,
  ProviderTemplatesResponse,
  ProvidersHealthResponse,
  ProvidersResponse,
  ReconciliationResponse,
  RoutingResponse,
} from '@/client/types/notifications-infra';

const BASE = '/api/admin/v2/notifications';
const HEALTH_TIMEOUT = { timeout: 45_000 };

export const notificationsInfraKeys = {
  all: ['notifications-infra-v2'] as const,
  overview: () => [...notificationsInfraKeys.all, 'overview'] as const,
  channels: () => [...notificationsInfraKeys.all, 'channels'] as const,
  providers: (channel?: string) =>
    [...notificationsInfraKeys.all, 'providers', channel ?? ''] as const,
  health: (provider?: string) =>
    [...notificationsInfraKeys.all, 'health', provider ?? ''] as const,
  routing: () => [...notificationsInfraKeys.all, 'routing'] as const,
  gates: () => [...notificationsInfraKeys.all, 'gates'] as const,
  reconciliation: () => [...notificationsInfraKeys.all, 'reconciliation'] as const,
  templates: (provider: string) =>
    [...notificationsInfraKeys.all, 'provider', provider, 'templates'] as const,
  template: (provider: string, id: string) =>
    [...notificationsInfraKeys.all, 'provider', provider, 'templates', id] as const,
  senders: (provider: string) =>
    [...notificationsInfraKeys.all, 'provider', provider, 'senders'] as const,
  events: (provider: string, p: object) =>
    [...notificationsInfraKeys.all, 'provider', provider, 'events', p] as const,
};

export const qs = (raw: Record<string, unknown>) => {
  const params = Object.entries(raw).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const s = new URLSearchParams(params).toString();
  return s ? `?${s}` : '';
};

/**
 * Admin v2 usually wraps payloads in `{ status, data }`. Accept either shape,
 * including a second wrap some proxies still emit.
 */
export const unwrapNotifications = <T>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    const inner = (raw as APIResponse<T>).data as unknown;
    if (inner != null && typeof inner === 'object' && 'data' in inner && 'status' in inner) {
      return (inner as APIResponse<T>).data as T;
    }
    return inner as T;
  }
  return raw as T;
};

const firstString = (...vals: unknown[]): string => {
  for (const v of vals) {
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return '';
};

const asRecord = (v: unknown): Record<string, unknown> | null =>
  v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

/**
 * Brevo (and other vendors) return camelCase (`htmlContent`, `isActive`).
 * Writes use snake_case. List endpoints often omit HTML entirely.
 */
export const normalizeProviderTemplate = (
  raw: unknown,
  fallbackId?: string | number,
): ProviderTemplate => {
  const rec = asRecord(raw);
  if (!rec) return { id: fallbackId ?? '' };

  const nested = asRecord(rec.template);
  const inner =
    nested && (nested.id != null || nested.htmlContent != null || nested.html_content != null)
      ? nested
      : rec;

  const senderRaw = inner.sender ?? inner.Sender;
  let sender: ProviderTemplate['sender'] = null;
  if (typeof senderRaw === 'string') {
    sender = {
      email: senderRaw,
      name: firstString(inner.sender_name, inner.senderName) || null,
    };
  } else {
    const s = asRecord(senderRaw);
    if (s) {
      sender = {
        name: firstString(s.name, s.Name) || null,
        email: firstString(s.email, s.Email) || null,
      };
    } else {
      const email = firstString(inner.sender_email, inner.senderEmail, inner.from, inner.from_email);
      const name = firstString(inner.sender_name, inner.senderName, inner.from_name);
      if (email || name) sender = { email: email || null, name: name || null };
    }
  }

  const html = firstString(
    inner.html_content,
    inner.htmlContent,
    inner.html,
    inner.content,
    inner.body,
  );

  return {
    ...(inner as ProviderTemplate),
    id: (inner.id ?? inner.template_id ?? inner.templateId ?? fallbackId ?? '') as string | number,
    name: firstString(inner.name, inner.Name) || null,
    subject: firstString(inner.subject, inner.Subject) || null,
    sender,
    html_content: html || null,
    is_active: (inner.is_active ?? inner.isActive) as boolean | number | undefined,
    reply_to: firstString(inner.reply_to, inner.replyTo) || null,
    tag: firstString(inner.tag, inner.Tag) || null,
    mapped_to: Array.isArray(inner.mapped_to)
      ? inner.mapped_to
      : Array.isArray(inner.mappedTo)
        ? inner.mappedTo
        : undefined,
    orphan: inner.orphan as boolean | undefined,
  };
};

const getErrorStatus = (error: unknown): number | undefined => {
  const err = error as { status?: number; statusCode?: number };
  return err?.status ?? err?.statusCode;
};

const getErrorCode = (error: unknown): string | undefined => {
  const err = error as {
    data?: { error?: string; data?: { error?: string } };
    response?: { data?: { error?: string; data?: { error?: string } } };
  };
  const code =
    err?.data?.data?.error ??
    err?.data?.error ??
    err?.response?.data?.data?.error ??
    err?.response?.data?.error;
  return typeof code === 'string' ? code : undefined;
};

export const useNotificationsOverviewQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.overview(),
    queryFn: withErrorHandling(async (): Promise<NotificationsOverview> => {
      const raw = await request<APIResponse<NotificationsOverview> | NotificationsOverview>(
        `${BASE}/overview`,
      );
      return unwrapNotifications(raw) ?? {};
    }, 'Failed to load delivery overview'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useNotificationChannelsQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.channels(),
    queryFn: withErrorHandling(async (): Promise<ChannelsResponse> => {
      const raw = await request<APIResponse<ChannelsResponse> | ChannelsResponse>(
        `${BASE}/channels`,
      );
      return unwrapNotifications(raw) ?? {};
    }, 'Failed to load channels'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useNotificationProvidersQuery = (channel?: string, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.providers(channel),
    queryFn: withErrorHandling(async (): Promise<ProvidersResponse> => {
      const raw = await request<APIResponse<ProvidersResponse> | ProvidersResponse>(
        `${BASE}/providers${qs({ channel })}`,
      );
      return unwrapNotifications(raw) ?? {};
    }, 'Failed to load providers'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useProvidersHealthQuery = (provider?: string, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.health(provider),
    queryFn: async (): Promise<ProvidersHealthResponse> => {
      try {
        const raw = await request<APIResponse<ProvidersHealthResponse> | ProvidersHealthResponse>(
          `${BASE}/providers/health${qs({ provider })}`,
          'GET',
          undefined,
          HEALTH_TIMEOUT,
        );
        return unwrapNotifications(raw) ?? {};
      } catch (error) {
        const status = getErrorStatus(error);
        const code = getErrorCode(error);
        if (status === 503 || code === 'probe_unavailable' || code === 'proxy_unavailable') {
          return { unavailable: true, error: code || 'probe_unavailable', providers: [] };
        }
        throw error;
      }
    },
    enabled,
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
    retry: false,
  });
};

export const useDeliveryRoutingQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.routing(),
    queryFn: withErrorHandling(async (): Promise<RoutingResponse> => {
      const raw = await request<APIResponse<RoutingResponse> | RoutingResponse>(
        `${BASE}/routing`,
      );
      return unwrapNotifications(raw) ?? {};
    }, 'Failed to load SMS routing'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useDeliveryGatesQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.gates(),
    queryFn: withErrorHandling(async (): Promise<GatesResponse> => {
      const raw = await request<APIResponse<GatesResponse> | GatesResponse>(`${BASE}/gates`);
      return unwrapNotifications(raw) ?? {};
    }, 'Failed to load delivery gates'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useReconciliationQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.reconciliation(),
    queryFn: withErrorHandling(async (): Promise<ReconciliationResponse> => {
      const raw = await request<APIResponse<ReconciliationResponse> | ReconciliationResponse>(
        `${BASE}/reconciliation`,
      );
      return unwrapNotifications(raw) ?? {};
    }, 'Failed to load reconciliation'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useProviderTemplatesQuery = (provider: string | undefined, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.templates(provider ?? ''),
    queryFn: withErrorHandling(async (): Promise<ProviderTemplatesResponse> => {
      const raw = await request<
        APIResponse<ProviderTemplatesResponse> | ProviderTemplatesResponse
      >(`${BASE}/providers/${encodeURIComponent(provider!)}/templates`);
      const data = unwrapNotifications<ProviderTemplatesResponse | ProviderTemplate[]>(raw);
      if (Array.isArray(data)) return { templates: data.map((t) => normalizeProviderTemplate(t)) };
      return {
        ...data,
        templates: (data?.templates ?? []).map((t) => normalizeProviderTemplate(t)),
      };
    }, 'Failed to load provider templates'),
    enabled: enabled && !!provider,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useProviderTemplateQuery = (
  provider: string | undefined,
  id: string | undefined,
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.template(provider ?? '', id ?? ''),
    queryFn: withErrorHandling(async (): Promise<ProviderTemplate> => {
      const raw = await request<
        APIResponse<ProviderTemplateDetailResponse | ProviderTemplate> | ProviderTemplate
      >(
        `${BASE}/providers/${encodeURIComponent(provider!)}/templates/${encodeURIComponent(id!)}`,
      );
      return normalizeProviderTemplate(
        unwrapNotifications<ProviderTemplateDetailResponse | ProviderTemplate>(raw),
        id,
      );
    }, 'Failed to load provider template'),
    enabled: enabled && !!provider && !!id,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useProviderSendersQuery = (provider: string | undefined, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.senders(provider ?? ''),
    queryFn: withErrorHandling(async (): Promise<ProviderSendersResponse> => {
      const raw = await request<APIResponse<ProviderSendersResponse> | ProviderSendersResponse>(
        `${BASE}/providers/${encodeURIComponent(provider!)}/senders`,
      );
      const data = unwrapNotifications<ProviderSendersResponse | ProviderSenderLike[]>(raw);
      if (Array.isArray(data)) return { senders: data };
      return data ?? {};
    }, 'Failed to load senders'),
    enabled: enabled && !!provider,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

type ProviderSenderLike = { email?: string; name?: string | null; verified?: boolean | number };

export const useProviderEventsQuery = (
  provider: string | undefined,
  params: ProviderEventsParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: notificationsInfraKeys.events(provider ?? '', params),
    queryFn: withErrorHandling(async (): Promise<ProviderEventsResponse> => {
      const raw = await request<APIResponse<ProviderEventsResponse> | ProviderEventsResponse>(
        `${BASE}/providers/${encodeURIComponent(provider!)}/events${qs(params as Record<string, unknown>)}`,
      );
      const data = unwrapNotifications<ProviderEventsResponse | ProviderEvent[]>(raw);
      if (Array.isArray(data)) return { events: data };
      return (data as ProviderEventsResponse) ?? {};
    }, 'Failed to load provider events'),
    enabled: enabled && !!provider,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
